import { AutoWithdrawTypeEnum, WageTypeEnum } from "@payping/api";
import {
  CheckOutlined,
  ChevronsUp,
  Close,
  CopyOutlined,
  DownOutlined,
  QrcodeOutlined,
  User as UserOutlined,
} from "@payping/icons";
import { addOverflow, safeJoinPath } from "@payping/utils/string";
import { webConstants } from "@payping/utils/platformConstants";
import { Avatar, Button, Col, Grid, Popover, PopoverImage, Row, Typography } from "@payping/web-components";
import { User } from "@payping/web-components/components/MainNav/types";
import { prefixCls } from "@payping/web-components/style/variables";
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import QRCode from "qrcode.react";
import classNames from "classnames";

import { useAppSelector } from "../../../utils/redux";
import styles from "../../../assets/scss/Profile.module.scss";
import planStyles from "../../../assets/scss/Plan.module.scss";
import { useAppTranslation } from "../../../utils/useAppTranslation";
import { PlanButton } from "./PlanButton";

interface Props {
  menu: React.ReactNode;
  user: User;
}

export const UserSection: React.FC<Props> = (props) => {
  const { menu, user } = props;
  const { t } = useAppTranslation();
  const [qrDataUrl, setQrDataUrl] = useState<string>();
  const [visibleModal, setVisibleModal] = useState<boolean>(false);
  const username = useAppSelector((state) => state.user.personalInfo?.username);
  const autoWithdrawType = useAppSelector((state) => state.user.currentPlan?.autoWithdrawType);
  const navigate = useNavigate();
  const location = useLocation();
  const wage = useAppSelector((state) => state.user.wage);

  const url = safeJoinPath(webConstants.PPNG, username || "");
  const qrDownloadUrl = "personal-page-qr";
  const isDesktop = Grid.useBreakpoint().lg;

  // close modal on route change
  useEffect(() => {
    setVisibleModal(false);
  }, [location.pathname]);

  const popoverContent = (
    <div className={planStyles.planPopover}>
      <Row>
        <Col span={24}>
          <Row justify={"center"}>
            <Avatar size={80} content={user?.profileImg || <UserOutlined width={"18px"} color={"white"} />} />
          </Row>
        </Col>
        <Col span={24}>
          <Row justify={"center"} style={{ margin: "0.4em 0" }}>
            <Typography.Text strong className={planStyles.planPopoverName}>
              {user?.fullName || user?.username}
            </Typography.Text>
            <PlanButton />
          </Row>
        </Col>
        <div className={planStyles.planFeaturesList}>
          <Typography.Text variant={"body2"}>
            {!wage?.pWage && !wage?.bWage
              ? t("plan.freeWage")
              : t(wage?.wageType === WageTypeEnum.Percentage ? "plan.wagePercent" : "plan.wageFixed", {
                  currency: t("toman"),
                  maximumWage: wage?.max,
                  subject: wage?.pWage ? t("plan.payer") : t("plan.receiver"),
                  wageValue: wage?.pWage || wage?.bWage,
                })}
          </Typography.Text>
          {autoWithdrawType !== AutoWithdrawTypeEnum.Disable ? (
            <Typography.Text variant={"body2"}>
              {t("plan.autoWithdraw", { hour: autoWithdrawType === AutoWithdrawTypeEnum.At11 ? "22" : "22:30" })}
            </Typography.Text>
          ) : null}
        </div>
        <Button
          block
          variant={"primary"}
          onClick={() => navigate(webConstants.DashboardRoutes.user.upgrade)}
          icon={ChevronsUp}
          size={"large"}
        >
          {t("plan.upgrade")}
        </Button>
      </Row>
      {username ? (
        <div className={styles.personalTypography}>
          <QRCode
            id={qrDownloadUrl}
            // this component is hidden from user, and is used for downloading a qr bigger than what user sees in dashboard
            style={{ display: "none" }}
            size={400}
            includeMargin={true}
            value={url}
          />
          <PopoverImage
            imageComponent={<QRCode size={80} includeMargin={true} value={url} />}
            onClick={() => {
              const canvas = document.getElementById(qrDownloadUrl) as HTMLCanvasElement;
              setQrDataUrl(canvas.toDataURL());
            }}
            label={
              <a id={"qr-anchor"} download={"PayPing-QR"} href={qrDataUrl}>
                {t("common.download")}
              </a>
            }
          >
            <div>
              <QrcodeOutlined className={classNames(`${prefixCls}-icon`, "color--cta")} width={"18px"} />
            </div>
          </PopoverImage>
          <a href={url} target={"_blank"} rel={"noreferrer"}>
            <Typography.Text
              className={styles.personalLink}
              copyable={{
                icon: [
                  <CopyOutlined key={0} width={"18px"} height={"18px"} />,
                  <CheckOutlined key={1} width={"14px"} height={"14px"} />,
                ],
                text: url,
              }}
            >
              {addOverflow(safeJoinPath(webConstants.PPNG, username), 20)}
            </Typography.Text>
          </a>
        </div>
      ) : (
        <Typography>{"-"}</Typography>
      )}
      {menu}
    </div>
  );
  const popoverAnchor = (
    <div onClick={() => setVisibleModal((prev) => !prev)} className={styles.profile}>
      <div className={styles.profileInfo}>
        <Avatar
          size={isDesktop ? "large" : "medium"}
          content={user?.profileImg || <UserOutlined width={"18px"} color={"white"} />}
        />
        {isDesktop && (
          <Typography.Text ellipsis className={styles.profileInfoName} color={"white"}>
            {user?.fullName || user?.username}
          </Typography.Text>
        )}
      </div>
      {isDesktop && (
        <>
          <PlanButton />
          <DownOutlined color={"white"} width={"12px"} />
        </>
      )}
    </div>
  );

  return isDesktop ? (
    <Popover visible={visibleModal} content={popoverContent}>
      {popoverAnchor}
    </Popover>
  ) : (
    <>
      <div onClick={() => setVisibleModal(true)}>{popoverAnchor}</div>
      <div
        className={classNames(styles.bottomModal, visibleModal ? "" : "hidden")}
        onClick={() => setVisibleModal(false)}
      >
        <div
          className={classNames(styles.modalContent, visibleModal ? "" : "hidden")}
          onClick={(event) => event.stopPropagation()}
        >
          <Close className={styles.modalClose} onClick={() => setVisibleModal(false)} width={"24"} />
          {popoverContent}
        </div>
      </div>
    </>
  );
};
